// CabinetStatusBar.tsx — per-Schaltschrank OPERATIONAL status above the Status map.
//
// One chip per cabinet, always driven by the LIVE latest snapshot (never a
// pinned historic frame): green dot + age of the cabinet's most recent real
// measurement while that age is within the deadman window; red cross +
// VERALTET tag once the cabinet hasn't measured for longer than the window.
// The question the bar answers is "is this cabinet operational right now" —
// frame-level staleness (carried-forward data) is the map's job, not the
// bar's. Single-cabinet installations (only the "" pseudo-cabinet) render
// nothing by default — the Status page relies on that; pass
// `singleCabinetLabel` to show that one cabinet as a chip under a generic
// label instead (the Dashboard does).
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CabinetStatus } from 'src/types';
import { parseUtcTimestamp } from 'src/utils/timestamps';
import './CabinetStatusBar.css';

interface CabinetStatusBarProps {
  cabinetStatus: CabinetStatus | null | undefined;
  // deadman_threshold is in HOURS everywhere (management form, backend alert
  // evaluator); default 24 matches the evaluator's DEFAULT_DEADMAN_HOURS.
  deadmanThresholdHours: number;
  // When set, the "" pseudo-cabinet of a single-cabinet installation is rendered
  // as a chip under this label instead of being hidden. The Status page omits
  // this (it has the map for that), the Dashboard passes t('status.cabinet.installation')
  // so single-cabinet installs get the same chip treatment as multi-cabinet ones.
  singleCabinetLabel?: string;
  // When set, each chip becomes a toggle button (used by the Dashboard to
  // expand the per-cabinet history table). `expandedCabinet` is the cabinet key
  // currently open, so the chip can render its pressed state.
  onCabinetClick?: (nr: string) => void;
  expandedCabinet?: string | null;
}

function formatAge(ms: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return t('status.cabinet.age', { age: `${Math.max(minutes, 0)} min` });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return t('status.cabinet.age', { age: `${hours} h` });
  return t('status.cabinet.age', { age: `${Math.floor(hours / 24)} d` });
}

const CabinetStatusBar = ({
  cabinetStatus,
  deadmanThresholdHours,
  singleCabinetLabel,
  onCabinetClick,
  expandedCabinet = null,
}: CabinetStatusBarProps) => {
  const { t } = useTranslation();

  // Wall-clock reference for the age labels; ticks once a minute so the
  // displayed ages stay current between snapshot polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const all = Object.entries(cabinetStatus ?? {});
  const real = all.filter(([nr]) => nr !== '');
  // Real cabinets win: an installation that reports numbered cabinets never
  // also shows the "" pseudo-cabinet, even when a label for it was supplied.
  const cabinets = real.length > 0 ? real : singleCabinetLabel ? all : [];
  if (cabinets.length === 0) return null;

  cabinets.sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <div className="cabinet-status-bar" data-testid="cabinet-status-bar">
      {cabinets.map(([nr, entry]) => {
        const last = parseUtcTimestamp(entry?.last_measurement);
        const ageMs = last ? now - last.getTime() : null;
        // Operational = the cabinet's own most recent measurement is younger
        // than the deadman window. The per-frame stale flag deliberately does
        // NOT make a chip red: a cabinet that merely missed the latest group
        // is still operational.
        const online = ageMs !== null && ageMs / 3_600_000 <= deadmanThresholdHours;
        // The "" pseudo-cabinet of a single-cabinet install is labeled with the
        // caller's generic label; numbered cabinets keep "Cabinet N"/"Schrank N".
        const label = nr === '' ? singleCabinetLabel : t('status.cabinet', { nr });
        const clickable = !!onCabinetClick;
        const expanded = expandedCabinet === nr;
        const inner = (
          <>
            <span className="cabinet-chip-indicator">{online ? '●' : '✖'}</span>
            <span className="cabinet-chip-label">{label}</span>
            <span className="cabinet-chip-age">
              {ageMs !== null ? formatAge(ageMs, t) : t('status.cabinet.never')}
            </span>
            {!online && <span className="cabinet-chip-stale">{t('status.cabinet.stale')}</span>}
            {clickable && (
              <span className="cabinet-chip-caret" aria-hidden="true">
                {expanded ? '▴' : '▾'}
              </span>
            )}
          </>
        );
        const className = `cabinet-chip ${online ? 'cabinet-chip--online' : 'cabinet-chip--offline'}${
          clickable ? ' cabinet-chip--clickable' : ''
        }${expanded ? ' cabinet-chip--expanded' : ''}`;

        if (clickable) {
          return (
            <button
              key={nr}
              type="button"
              data-cabinet={nr}
              className={className}
              title={entry?.last_measurement ?? undefined}
              aria-expanded={expanded}
              onClick={() => onCabinetClick(nr)}
            >
              {inner}
            </button>
          );
        }

        return (
          <span
            key={nr}
            data-cabinet={nr}
            className={className}
            title={entry?.last_measurement ?? undefined}
          >
            {inner}
          </span>
        );
      })}
    </div>
  );
};

export default CabinetStatusBar;
